<template>
  <v-container fluid>
    <v-row row>
      <v-col class="s8 input-field no-margins">

        <div v-if="!valid">
          This page is not valid for this site group
        </div>
        
        <div v-else align="center">
          <PortfolioManagerTable :randomKey="randomKey"></PortfolioManagerTable>
        </div>
      </v-col>
    </v-row>

  
  </v-container>
</template>

<script lang="ts">

import { Component, Ref, Vue } from 'vue-property-decorator';
import FaceRating from '@/components/FaceRating.vue';

import { strip_surrounding_quotes } from '../main';
import PortfolioManagerTable from './PortfolioManagerTable.vue';
import FullScreenLoad from '@/components/FullScreenLoad.vue';
import * as common from '@/common';
import { DateTime } from "luxon";
import { IChartRef, ILine, IDataPoint } from '@/components/ShimmyPlus/ChartInterfaces';
import base64url from 'base64url';

export function object_to_baseurl64(refs: IChartRef[]): string {
  return base64url.encode(JSON.stringify(refs));
}

@Component({
  components: {
    FaceRating,
    FullScreenLoad,
    PortfolioManagerTable,
  },
})
export default class PortfolioManager extends Vue {

  public $refs!: {
    form: any;
  };

  private headers: object[] = [
    {
      text: 'Property',
      align: 'center',
      sortable: true,
      value: 'property_name',
    },
    { text: 'Issues', units: '', align: 'center', value: 'issues'},
    { text: 'Energy', units: '(kWh / m²  / year)', align: 'center', value: 'energy_12months'},
    { text: 'Comfort', units: '(Average ℃)', align: 'center', value: 'comfort_12months' },
    { text: 'Hot Water', units: '(Litres / day)', align: 'center', value: 'hotwater_12months' },
    { text: 'Appliances', units: '(kWh / day)', align: 'center', value: 'appliances_12months' },
    { text: ' ', units: ' ', align: 'center', value: '--' },
    { text: 'Energy', units: '(kWh / m²  / year)', align: 'center', value: 'energy_30days' },
    { text: 'Comfort', units: '(Average ℃)', align: 'center', value: 'comfort_30days' },
    { text: 'Hot Water', units: '(Litres / day)', align: 'center', value: 'hotwater_30days' },
    { text: 'Appliances', units: '(kWh / day)', align: 'center', value: 'appliances_30days' },
  ];

  private colour_map: object = {"carnego_heating_last_12_months": 22.0,
                              "carnego_comfort_last_12_months": 1000.0,
                              "carnego_hotwater_last_12_months": 22.0,
                              // "carnego_appliance_last_12_months": ,
                              "carnego_heating_lastmonth": 22.0,
                              "carnego_comfort_lastmonth": 1000.0,
                              "carnego_hotwater_lastmonth": 22.0,
                              // "carnego_appliance_lastmonth"
  };

  private valid: boolean = false;
  private randomKey: string = '';
  private items_per_page: number = -1;
  private data: any[] = [];
  private search: string = '';
  private showWait: boolean = true;
  private automode: [number, string, string, string] = [-1, 'red', 'Unknown Error', 'mdi-radiator-off'];
  private json_data: string = '';
  private last_run: string = '';

  private colours: Map<string, string> = new Map();

  public async created() {
    const self = this;

    self.colours = new Map([
      ['OK', 'green'],
      ['!', 'orange'],
      ['!!!', 'red'],
    ]);
  }

  public async mounted() {
    const self = this;

    await self.update();

    this.$store.watch(
      (state) => {
        return this.$store.state.currentSiteGroup; // could also put a Getter here
      },
      (newValue, oldValue) => {

        self.update();
        self.randomKey = (Math.random() + 1).toString(36).substring(7);
      },
    );
  }

  public async update() {
    const self = this;

    self.json_data = await self.$store.dispatch('requestStaticFileForSiteGroupId', {"product": "tagged_portfolio_generator", "filename": "portfolio.json"}, {timeout: 30000}); 

    if(self.json_data) {
      self.valid = true;
    }

    const lines = self.json_data.split('\n');

    const last_run_header = lines[0];

    const last_time_str = JSON.parse(last_run_header)['last_run'];

    // 2022-07-11T11:05:06 +0100
    const dt = DateTime.fromFormat(
              last_time_str,
              "yyyy-MM-dd'T'HH:mm:ss ZZZ"
            );
              
    self.last_run = dt.toFormat('dd-MM-yyyy HH:mm');

    for (const line of lines.slice(1)) {

      if(line === undefined || line === "") {
        continue;
      }

      let line_json = JSON.parse(line);
      line_json["issues"] = "sad;sad;sad;sad;";

      const item_obj = {};

        const site_name = common.split_namespace_name(line_json['site']);

        item_obj['site'] = {display: site_name, style: { 'background-color': 'rgba(100, 100, 100, 0)' } };
        item_obj['issues'] = {display: "sad;sad;sad;sad;", style: { 'background-color': 'rgba(100, 100, 100, 0)' } };

        item_obj['carnego_heating_last_12_months'] = {display: self.parse_float_or_str(line_json['carnego_heating_last_12_months'], "NaN"), style: { 'background-color': self.calcColour('carnego_heating_last_12_months', line_json['carnego_heating_last_12_months']), refs: self.baseurl64_encode(line_json["carnego_heating_refs"]) }};
        item_obj['carnego_comfort_last_12_months'] = {display: self.parse_float_or_str(line_json['carnego_comfort_last_12_months'], "NaN"), style: { 'background-color': self.calcColour('carnego_comfort_last_12_months', line_json['carnego_comfort_last_12_months']), refs: self.baseurl64_encode(line_json["carnego_comfort_refs"]) }};
        item_obj['carnego_hotwater_last_12_months'] = {display: self.parse_float_or_str(line_json['carnego_hotwater_last_12_months'], "NaN"), style: { 'background-color': self.calcColour('carnego_hotwater_last_12_months', line_json['carnego_hotwater_last_12_months']), refs: self.baseurl64_encode(line_json["carnego_hotwater_refs"]) }};
        item_obj['carnego_appliance_last_12_months'] = {display: self.parse_float_or_str(line_json['carnego_appliance_last_12_months'], "NaN"), style: { 'background-color': self.calcColour('carnego_appliance_last_12_months', line_json['carnego_appliance_last_12_months']), refs: self.baseurl64_encode(line_json["carnego_appliance_refs"]) }};
        
        item_obj['carnego_heating_lastmonth'] = {display: self.parse_float_or_str(line_json['carnego_heating_lastmonth'], "NaN"), style: { 'background-color': self.calcColour('carnego_heating_lastmonth', line_json['carnego_heating_lastmonth']), refs: self.baseurl64_encode(line_json["carnego_heating_refs"]) }};
        item_obj['carnego_comfort_lastmonth'] = {display: self.parse_float_or_str(line_json['carnego_comfort_lastmonth'], "NaN"), style: { 'background-color': self.calcColour('carnego_comfort_lastmonth', line_json['carnego_comfort_lastmonth']), refs: self.baseurl64_encode(line_json["carnego_comfort_refs"]) }};
        item_obj['carnego_hotwater_lastmonth'] = {display: self.parse_float_or_str(line_json['carnego_hotwater_lastmonth'], "NaN"), style: { 'background-color': self.calcColour('carnego_hotwater_lastmonth', line_json['carnego_hotwater_lastmonth']), refs: self.baseurl64_encode(line_json["carnego_hotwater_refs"]) }};
        item_obj['carnego_appliance_lastmonth'] = {display: self.parse_float_or_str(line_json['carnego_appliance_lastmonth'], "NaN"), style: { 'background-color': self.calcColour('carnego_appliance_lastmonth', line_json['carnego_appliance_lastmonth']), refs: self.baseurl64_encode(line_json["carnego_appliance_refs"]) }};
        
      console.log(item_obj);
      self.data.push(item_obj);
    }

    console.log(self.data);

    self.showWait = false;
  }

  public link(reference: string): string {
    return '/#/portfolio_manager_details/' + reference;
  }

  public validate() {
    this.$refs.form.validate();
  }

  public reset() {
    this.$refs.form.reset();
  }

  public resetValidation() {
    this.$refs.form.resetValidation();
  }

  protected baseurl64_encode(refs: IChartRef[]) {
    return object_to_baseurl64(refs);
  }

  protected async getRefsFromSiteGroupSitesAndNamesPayload(): Promise<object> {
    return {requested_site_group_namespace: 'bryn_bragl', point_names: ['downstairs_sockets_energy', 'upstairs_sockets_energy', 'kitchen_sockets_energy',
                        'actual_room_temp', 'heating_energy', 'dhw_volume']};
  }

  private calcColour(k: string, s: string): string {

    const self = this;

    if(s === undefined) {
      return 'red';
    }

    if (this.isFloat(s)) {
      const value = parseFloat(s);

      if (k in self.colour_map) {
        if(value >= self.colour_map[k]) {
          return 'red';
        }
      }

    }

    return 'green';
  }

  private isFloat(s: string): boolean {
    var floatRegex = /^-?\d+(?:[.,]\d*?)?$/;
    if (floatRegex.test(s))
        return true;

    // Handle scientifc notation ?
    const val = parseFloat(s);
    if (isNaN(val))
        return false;
    return true;
  }

  private parse_float_or_str(s: string, default_string: string): string {

    if(s === undefined) {
      return default_string;
    }

    if (this.isFloat(s)) {
      return parseFloat(s).toFixed(1);
    }

    s = s.trim();

    if (s === "None") {
      s = default_string;
    }

    return s;
  }

  private parse_field(s: any, default_colour='rgba(100, 100, 100, 1)'): [string, string] {

    const self = this;

    if (s === undefined || s === null) {
      return ['unknown', 'unknown'];
    }

    if (typeof s === 'string' || s instanceof String) {
      s = s.trim();
    }

    if (Array.isArray(s)) {
      return [self.parse_float_or_str(s[0], s[1]), self.colours.get(s[1])!];
    }

    console.log("typeof s: ", typeof s, s)

    // Normal float or string
    const tmp: [string, string] = [self.parse_float_or_str(s, s), default_colour];
    // console.log('tmp:', tmp);
    return tmp;
  }

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="css">
</style>